import { useState, useEffect } from "react";
import { AddressAutocomplete } from "./AddressAutocomplete";
import RideMap from "./RideMap";
import { Input } from "./ui/input";
import api from "../lib/api";
import { Car, Bike, Clock, Package, UtensilsCrossed, Leaf, Navigation, Loader2, Star, Search, CheckCircle2, Pencil, SearchX, TrafficCone } from "lucide-react";

const PINK = "#EC407A";
const ECO_GREEN = "#16a34a";

export default function EuropeRideForm({
  region, pickup, setPickup, dest, setDest, pickupCoords, setPickupCoords, destCoords, setDestCoords,
  mode, setMode, quotes, money, loading, onBook, mapCenter, onMapClick, locateMe, locating,
  item, setItem, recipientName, setRecipientName, recipientPhone, setRecipientPhone,
  restaurantName, setRestaurantName, dishes, setDishes,
}) {
  const [step, setStep] = useState("search"); // search | searching | found | none
  const [foundDriver, setFoundDriver] = useState(null);
  // Nold Go Europe : le vélo (livraison repas) est retiré du VTC. On force "car" si legacy.
  useEffect(() => { if (mode === "velo") setMode("car"); }, [mode, setMode]);
  const isVelo = false;
  const hasCoords = !!(pickupCoords && destCoords);
  const parcelOk = true;
  const canSearch = pickup?.trim() && dest?.trim() && hasCoords && parcelOk;
  const price = quotes[mode];

  // Suggestions restaurants partenaires (annuaire évolutif — vide si aucun partenaire encore référencé).
  const [restoSuggestions, setRestoSuggestions] = useState([]);
  const [restoOpen, setRestoOpen] = useState(false);
  useEffect(() => {
    if (!isVelo || !restaurantName || restaurantName.length < 2) { setRestoSuggestions([]); return; }
    const ctl = new AbortController();
    const t = setTimeout(() => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/restaurants?q=${encodeURIComponent(restaurantName)}&limit=6`, { signal: ctl.signal })
        .then((r) => r.json()).then((j) => setRestoSuggestions(j.items || [])).catch(() => {});
    }, 250);
    return () => { ctl.abort(); clearTimeout(t); };
  }, [restaurantName, isVelo]);

  const [traffic, setTraffic] = useState(null);
  useEffect(() => {
    let ok = true;
    const load = () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/traffic/be`)
      .then((r) => r.json()).then((j) => { if (ok) setTraffic(j); }).catch(() => {});
    load();
    const t = setInterval(load, 180000);
    return () => { ok = false; clearInterval(t); };
  }, []);

  const services = [
    { id: "car", label: "Voiture Eco", sub: "Jusqu'à 4 places", Icon: Car, price: quotes.car, color: PINK },
    { id: "comfort", label: "Comfort", sub: "Véhicule haut de gamme", Icon: Car, price: quotes.comfort, color: PINK },
    { id: "xl", label: "XL", sub: "Van 6 places · groupes & bagages", Icon: Car, price: quotes.xl, color: PINK },
  ];

  // Recherche d'un VRAI chauffeur en ligne à proximité (Bruxelles/Europe).
  const search = async () => {
    if (!canSearch) return;
    setStep("searching");
    try {
      const vt = isVelo ? "bike" : "car";
      const { data } = await api.get(`/drivers/nearby`, { params: { lat: pickupCoords.lat, lng: pickupCoords.lng, vehicle_type: vt } });
      if (data && data.length) {
        const d = data[0];
        const km = d.distance_km != null ? d.distance_km : 2;
        setFoundDriver({
          name: d.name || "Chauffeur",
          vehicle: isVelo ? "Vélo / Fiets" : "Voiture / Auto",
          plate: d.plate || "",
          rating: d.rating || 5,
          eta: Math.max(1, Math.round((km / (isVelo ? 15 : 30)) * 60)),
          dist: km,
        });
        setStep("found");
      } else {
        setFoundDriver(null);
        setStep("none");
      }
    } catch {
      setFoundDriver(null);
      setStep("none");
    }
  };
  const backToSearch = () => setStep("search");
  const etaShown = foundDriver?.eta ?? 5;

  return (
    <div className="space-y-4" data-testid="eu-order-form">
      {traffic && (
        <div data-testid="eu-traffic-card" className="rounded-2xl border border-[#E2DFD8] bg-white p-3 flex items-start gap-3">
          <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#2563EB]/10 text-[#2563EB] shrink-0"><TrafficCone className="w-5 h-5" /></span>
          <div className="min-w-0 text-sm">
            <div className="font-bold text-[#1A1A1A]">Trafic Belgique · temps réel</div>
            {(traffic.jam_count > 0 || traffic.roadworks_count > 0) ? (
              <div className="text-[#5A635D] mt-0.5 leading-relaxed">
                {(traffic.jams || []).slice(0, 2).map((j, i) => (
                  <span key={i} data-testid={`eu-traffic-jam-${i}`} className="mr-3 whitespace-nowrap">🚗 Bouchon {j.length_km} km <span className="text-[#F26419] font-semibold">~{j.delay_min} min</span></span>
                ))}
                {traffic.roadworks_count > 0 && <span data-testid="eu-traffic-roadworks" className="whitespace-nowrap">🚧 {traffic.roadworks_count} chantiers</span>}
                <span className="block text-[#F26419] font-semibold mt-0.5">⚠️ Roulez prudemment</span>
              </div>
            ) : (
              <div data-testid="eu-traffic-fluid" className="text-[#16a34a] mt-0.5 font-semibold">🟢 Trafic fluide</div>
            )}
          </div>
        </div>
      )}
      {step === "search" && (
        <div className="bg-white rounded-3xl border border-[#ECE7DF] p-3 shadow-[0_2px_16px_rgba(0,0,0,0.04)] space-y-2.5">
          <AddressAutocomplete testid="eu-pickup" value={pickup} country={region?.country_code}
            onChange={(v) => { setPickup(v); setPickupCoords(null); }} placeholder="Point de départ" dotColor="#16a34a"
            onSelect={(r) => { setPickup(r.label); setPickupCoords({ lat: r.lat, lng: r.lng }); }} />
          <AddressAutocomplete testid="eu-dest" value={dest} country={region?.country_code}
            onChange={(v) => { setDest(v); setDestCoords(null); }} placeholder="Où allez-vous ?" dotColor={PINK}
            onSelect={(r) => { setDest(r.label); setDestCoords({ lat: r.lat, lng: r.lng }); }} />
        </div>
      )}

      <div className="relative rounded-3xl overflow-hidden border border-[#ECE7DF]" data-testid="eu-map">
        <RideMap center={mapCenter} pickup={pickupCoords} dest={destCoords} drivers={[]} onMapClick={step === "search" ? onMapClick : undefined} height={280} />
        <div className="absolute top-3 left-3 z-[1000] inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-bold text-[#1A1A1A] shadow" data-testid="eu-wait">
          <Clock className="w-3.5 h-3.5" style={{ color: PINK }} /> Arrivée dans ~{etaShown} min
        </div>
        {step === "search" && (
          <button type="button" data-testid="eu-locate" onClick={locateMe} disabled={locating}
            className="absolute bottom-3 right-3 z-[1000] w-11 h-11 rounded-full bg-white shadow flex items-center justify-center disabled:opacity-60" style={{ color: PINK }}>
            <Navigation className="w-5 h-5" />
          </button>
        )}
      </div>

      {step === "search" && (
        <>
          {isVelo && (
            <>
              <div data-testid="eu-eco-banner" className="rounded-2xl border border-[#16a34a]/40 bg-[#16a34a]/10 p-3 flex items-center gap-3">
                <span className="w-9 h-9 shrink-0 rounded-xl bg-[#16a34a] flex items-center justify-center text-white"><Leaf className="w-5 h-5" /></span>
                <div className="min-w-0 text-sm">
                  <div className="font-bold text-[#16a34a]">🚴‍♂️ Livraison rapide & Écologique</div>
                  <div className="text-[#3B423E] text-xs">Repas chauds livrés à vélo par nos coursiers Nold Go — zéro émission.</div>
                </div>
              </div>
              <div className="bg-white rounded-3xl border border-[#ECE7DF] p-4 space-y-3" data-testid="eu-parcel-fields">
                <div className="relative">
                  <UtensilsCrossed className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ECO_GREEN }} />
                  <Input data-testid="eu-restaurant" value={restaurantName} onChange={(e) => { setRestaurantName(e.target.value); setRestoOpen(true); }}
                    onFocus={() => setRestoOpen(true)} onBlur={() => setTimeout(() => setRestoOpen(false), 150)}
                    placeholder="Nom du restaurant (ex : Pizzeria Roma)" className="h-12 rounded-2xl border-2 pl-10" />
                  {restoOpen && restoSuggestions.length > 0 && (
                    <div data-testid="eu-restaurant-suggestions" className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-[#ECE7DF] rounded-2xl shadow-lg overflow-hidden">
                      {restoSuggestions.map((r) => (
                        <button key={r.id} type="button" data-testid={`eu-resto-opt-${r.id}`}
                          onClick={() => { setRestaurantName(r.name); setRestoOpen(false); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-[#F7F3EC] text-sm">
                          <div className="font-semibold text-[#1A1A1A]">{r.name}</div>
                          {(r.cuisine || r.city) && <div className="text-[11px] text-[#75807A]">{[r.cuisine, r.city].filter(Boolean).join(" · ")}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Package className="absolute left-3.5 top-3 w-4 h-4" style={{ color: ECO_GREEN }} />
                  <textarea data-testid="eu-dishes" value={dishes} onChange={(e) => setDishes(e.target.value)}
                    placeholder="Liste des plats (ex : 2 pizzas margherita, 1 salade César, 2 cocas)"
                    className="w-full min-h-[72px] rounded-2xl border-2 border-input pl-10 pr-3 py-2.5 text-sm outline-none focus:border-[#16a34a] resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <Input data-testid="eu-recipient-name" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Votre nom" className="h-12 rounded-2xl border-2" />
                  <Input data-testid="eu-recipient-phone" value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} placeholder="Téléphone" className="h-12 rounded-2xl border-2" />
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3" data-testid="eu-service-selector">
            {services.map((s) => {
              const active = mode === s.id;
              return (
                <button key={s.id} type="button" data-testid={`eu-service-${s.id}`} onClick={() => setMode(s.id)}
                  className={`rounded-3xl border-2 p-4 text-left transition-all ${active ? (s.id === "velo" ? "border-[#16a34a] bg-[#16a34a]/10" : "border-[#EC407A] bg-[#FCE4EC]") : "border-[#ECE7DF] bg-white hover:border-[#EC407A]/50"}`}>
                  <s.Icon className="w-6 h-6" style={{ color: active ? s.color : "#75807A" }} />
                  <div className="mt-2 font-head font-bold text-sm text-[#1A1A1A]">{s.label}</div>
                  <div className="text-[11px] text-[#75807A]">{s.sub}</div>
                  <div className="mt-1 font-head font-black text-lg text-[#1A1A1A]" data-testid={`eu-price-${s.id}`}>{s.price != null ? money(s.price) : "—"}</div>
                </button>
              );
            })}
          </div>

          <button type="button" data-testid="eu-search-btn" onClick={search} disabled={!canSearch}
            className="w-full h-16 rounded-2xl text-white font-head font-black text-lg flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
            style={{ background: PINK }}>
            <Search className="w-5 h-5" /> {isVelo ? "Trouver mon coursier" : "Rechercher un chauffeur"}
          </button>
          {!hasCoords && (pickup?.trim() || dest?.trim()) && (
            <p className="text-center text-[11px] text-[#EC407A] font-medium">Choisissez une adresse dans la liste pour continuer</p>
          )}
        </>
      )}

      {step === "searching" && (
        <div className="bg-white rounded-3xl border border-[#ECE7DF] p-8 text-center" data-testid="eu-searching">
          <Loader2 className="w-12 h-12 mx-auto animate-spin" style={{ color: PINK }} />
          <div className="mt-4 font-head font-black text-lg text-[#1A1A1A]">{isVelo ? "Recherche d'un coursier…" : "Recherche d'un chauffeur…"}</div>
          <div className="mt-1 text-sm text-[#75807A]">Nous contactons les {isVelo ? "coursiers vélo" : "voitures"} en ligne près de vous</div>
        </div>
      )}

      {step === "none" && (
        <div className="bg-white rounded-3xl border border-[#ECE7DF] p-8 text-center" data-testid="eu-no-driver">
          <SearchX className="w-12 h-12 mx-auto text-[#75807A]" />
          <div className="mt-4 font-head font-black text-lg text-[#1A1A1A]">{isVelo ? "Aucun coursier disponible" : "Aucun chauffeur disponible"}</div>
          <div className="mt-1 text-sm text-[#75807A]">Aucun {isVelo ? "coursier vélo" : "chauffeur"} en ligne à proximité pour le moment. Réessayez dans un instant.</div>
          <button type="button" data-testid="eu-retry-btn" onClick={backToSearch} className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-2xl text-white font-bold" style={{ background: PINK }}>
            <Search className="w-4 h-4" /> Réessayer
          </button>
        </div>
      )}

      {step === "found" && foundDriver && (
        <>
          <div className="bg-white rounded-3xl border-2 border-[#EC407A] p-5" data-testid="eu-driver-found">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#16a34a] uppercase tracking-wide"><CheckCircle2 className="w-3.5 h-3.5" /> {isVelo ? "Coursier trouvé" : "Chauffeur trouvé"}</div>
            <div className="mt-3 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#FCE4EC] flex items-center justify-center font-head font-black text-[#EC407A]">{(foundDriver.name || "C").charAt(0).toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <div className="font-head font-bold text-[#1A1A1A]">{foundDriver.name}</div>
                <div className="text-xs text-[#75807A] truncate">{foundDriver.vehicle}{foundDriver.plate ? ` · ${foundDriver.plate}` : ""}</div>
              </div>
              <div className="text-right">
                <div className="inline-flex items-center gap-1 text-sm font-bold text-[#1A1A1A]"><Star className="w-3.5 h-3.5 fill-[#FFCC00] text-[#FFCC00]" /> {Number(foundDriver.rating).toFixed(1)}</div>
                <div className="text-[11px] text-[#75807A] flex items-center gap-1 justify-end"><Clock className="w-3 h-3" /> ~{foundDriver.eta} min</div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-[#F3EFE8] pt-3">
              <div className="text-sm text-[#75807A]">{isVelo ? "Livraison Repas · Vélo" : "Voiture Eco"}</div>
              <div className="font-head font-black text-2xl text-[#1A1A1A]" data-testid="eu-found-price">{price != null ? money(price) : "—"}</div>
            </div>
            <button type="button" data-testid="eu-edit-btn" onClick={backToSearch} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#75807A] hover:text-[#EC407A] transition-colors">
              <Pencil className="w-3.5 h-3.5" /> Modifier ma course
            </button>
          </div>

          <button type="button" data-testid="eu-reserve-btn" onClick={onBook} disabled={loading}
            className="w-full h-16 rounded-2xl text-white font-head font-black text-lg flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
            style={{ background: PINK }}>
            {loading ? "…" : `Réserver • Payer ${price != null ? money(price) : ""}`}
          </button>
          <p className="text-center text-[11px] text-[#75807A]">Paiement sécurisé par carte via Stripe · {region?.country_code === "FR" ? "France" : "Belgique"}</p>
        </>
      )}
    </div>
  );
}
